import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, DollarSign, TrendingUp, Sparkles, Handshake } from "lucide-react";
import { LogoIcon } from "@/components/Wordmark";

// Public marketing page for the TradeGreen influencer / affiliate program.
// No auth required — anyone can land here from a tweet/YouTube description.
// CTA is a contact/apply link (mailto for launch, can wire up a self-serve
// application form later).
export default function Partners() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--fg)" }}>
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-4 flex items-center gap-2">
          <LogoIcon size={18} />
          <Handshake className="h-4 w-4" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
          <div className="label-eyebrow" style={{ color: "var(--accent)" }}>TRADEGREEN PARTNERS · INFLUENCER PROGRAM</div>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight mb-6">
          Get paid every month, for every trader you send us.
        </h1>
        <p className="text-base leading-relaxed mb-10 max-w-2xl" style={{ color: "var(--fg-2)" }}>
          If you influence traders on Twitter, YouTube, Discord, or TikTok — turn that reach into recurring
          revenue. We pay 25% of every dollar your referrals spend on TradeGreen, forever.
        </p>

        {/* Offer cards */}
        <div className="grid md:grid-cols-2 gap-4 mb-12">
          <div
            className="p-6"
            style={{
              background: "linear-gradient(180deg, rgba(34,197,94,0.06), var(--surface) 60%)",
              border: "1px solid rgba(34,197,94,0.35)",
              borderRadius: "14px",
            }}
            data-testid="partner-offer-influencer"
          >
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4" strokeWidth={1.5} style={{ color: "var(--green)" }} />
              <div className="label-eyebrow" style={{ color: "var(--green)" }}>WHAT YOU GET</div>
            </div>
            <ul className="space-y-2 text-sm mt-3" style={{ color: "var(--fg)" }}>
              <li>· <strong>Free lifetime Pro</strong> access (real cost to you: $0)</li>
              <li>· <strong>25% recurring commission</strong> on every referred customer</li>
              <li>· Paid monthly, no minimum threshold</li>
              <li>· Real-time dashboard: signups, MTD earnings, lifetime commission</li>
              <li>· No exclusivity clause — promote whoever you want</li>
            </ul>
          </div>

          <div
            className="p-6"
            style={{
              background: "linear-gradient(180deg, rgba(168,85,247,0.06), var(--surface) 60%)",
              border: "1px solid rgba(168,85,247,0.35)",
              borderRadius: "14px",
            }}
            data-testid="partner-offer-customer"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
              <div className="label-eyebrow" style={{ color: "var(--accent)" }}>WHAT YOUR AUDIENCE GETS</div>
            </div>
            <ul className="space-y-2 text-sm mt-3" style={{ color: "var(--fg)" }}>
              <li>· <strong>20% off for the first 3 months</strong></li>
              <li>· Full 7-day free trial still applies</li>
              <li>· Any tier (Starter or Pro)</li>
              <li>· Auto-applied when they click your link</li>
              <li>· Cancel anytime — no lock-in</li>
            </ul>
          </div>
        </div>

        {/* Math example */}
        <div
          className="p-6 mb-12"
          style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "14px" }}
        >
          <div className="label-eyebrow mb-3" style={{ color: "var(--fg-3)" }}>THE MATH · REAL NUMBERS</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-[11px] tracking-widest mb-1" style={{ color: "var(--fg-3)" }}>REFERRALS</div>
              <div className="font-mono-num text-2xl" style={{ color: "var(--fg)" }}>50</div>
            </div>
            <div>
              <div className="text-[11px] tracking-widest mb-1" style={{ color: "var(--fg-3)" }}>ON PRO</div>
              <div className="font-mono-num text-2xl" style={{ color: "var(--fg)" }}>$29.99</div>
              <div className="text-[10px]" style={{ color: "var(--fg-3)" }}>per user / mo</div>
            </div>
            <div>
              <div className="text-[11px] tracking-widest mb-1" style={{ color: "var(--fg-3)" }}>YOUR CUT</div>
              <div className="font-mono-num text-2xl" style={{ color: "var(--green)" }}>25%</div>
            </div>
            <div>
              <div className="text-[11px] tracking-widest mb-1" style={{ color: "var(--fg-3)" }}>YOU EARN</div>
              <div className="font-mono-num text-2xl" style={{ color: "var(--green)" }}>$374</div>
              <div className="text-[10px]" style={{ color: "var(--fg-3)" }}>/ month, recurring</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <a
            href="mailto:joshwilliamstrades@gmail.com?subject=TradeGreen%20Partner%20Application&body=Name:%0AAudience%20size:%0AWhere%20you'll%20promote%20(YouTube%2C%20Twitter%2C%20Discord):%0AWhy%20us:"
            data-testid="partner-apply-cta"
            className="inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold tracking-wide transition-all"
            style={{
              background: "var(--accent)",
              color: "white",
              borderRadius: "12px",
              boxShadow: "var(--accent-glow)",
            }}
          >
            <Sparkles className="h-4 w-4" strokeWidth={1.5} />
            APPLY TO BECOME A PARTNER
            <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </a>
          <div className="mt-4 text-xs" style={{ color: "var(--fg-3)" }}>
            Already a partner? <button onClick={() => navigate("/partners/dashboard")} className="underline" data-testid="partner-dashboard-link">See your dashboard →</button>
          </div>
        </div>
      </div>
    </div>
  );
}
